// pages/AnalyticsDashboardPage.jsx
// Analytics Dashboard - REAL DATA via GraphQL
// ============================================
// UPDATED: Jan 17, 2026 - Fixed achievements to only show EARNED ones (Issue 1)

import React, { useState } from 'react';
import { ArrowLeft, TrendingUp, DollarSign, Award, Users, Download, RefreshCw, AlertCircle, Coins, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/common/BottomNav';

// Import chart components
import BarChart from '../components/analytics/BarChart';
import LineChart from '../components/analytics/LineChart';
import PieChart from '../components/analytics/PieChart';
import StatCard from '../components/analytics/StatCard';

// GraphQL Query for user analytics
const GET_USER_ANALYTICS = gql`
  query GetUserAnalytics($timeRange: String) {
    userAnalytics(timeRange: $timeRange) {
      # Savings
      totalSaved
      periodSaved
      monthSaved
      yearSaved
      lifetimeSaved
      
      # Tokens
      tokensEarned
      lifetimeTokensEarned
      lifetimeTokensRedeemed
      tokenBalance
      
      # Jobs
      jobsCompleted
      lifetimeJobsCompleted
      completionRate
      bestMonth
      
      # Groups
      totalReferrals
      successfulReferrals
      referralEarnings
      
      # Trust
      trustScore
      
      # Chart data
      savingsByCategory {
        label
        value
        color
      }
      earningsOverTime {
        label
        value
      }
      jobsByType {
        label
        value
        color
      }
      savingsTrend {
        label
        value
      }
      
      # Computed
      avgSavingsPerPurchase
      avgTokensPerJob
      localValueCreated
      
      # Metadata
      timeRange
      generatedAt
    }
  }
`;

// GraphQL Query for user's ACTUAL achievements
const GET_USER_ACHIEVEMENTS = gql`
  query GetUserAchievements {
    userAchievements {
      achieved {
        id
        name
        description
        icon
        tokens
        category
        achievedAt
      }
      totalAchieved
      totalAvailable
      percentComplete
    }
  }
`;

const AnalyticsDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState('month');

  // Fetch real analytics data
  const { data, loading, error, refetch } = useQuery(GET_USER_ANALYTICS, {
    variables: { timeRange },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  // Fetch REAL achievements
  const { data: achievementsData, loading: achievementsLoading } = useQuery(GET_USER_ACHIEVEMENTS, {
    fetchPolicy: 'cache-and-network'
  });

  const analytics = data?.userAnalytics;
  const achievements = achievementsData?.userAchievements;

  // Export to CSV
  const handleExport = () => {
    if (!analytics) return;

    const csvData = `
UniLedge Analytics Report
Generated: ${new Date().toLocaleDateString()}
Member: ${user?.name || 'Member'}
Time Range: ${timeRange}

Summary Statistics
Total Lifetime Saved: $${analytics.lifetimeSaved}
This Year: $${analytics.yearSaved}
This Month: $${analytics.monthSaved}
This ${timeRange}: $${analytics.periodSaved}
Tokens Earned (Period): ${analytics.tokensEarned}
Lifetime Tokens Earned: ${analytics.lifetimeTokensEarned}
Lifetime Tokens Redeemed: ${analytics.lifetimeTokensRedeemed || 0}
Current Token Balance: ${analytics.tokenBalance}
Token Value: $${((analytics.tokenBalance || 0) / 10).toFixed(2)}
Jobs Completed (Period): ${analytics.jobsCompleted}
Lifetime Jobs: ${analytics.lifetimeJobsCompleted}
Total Referrals: ${analytics.totalReferrals}
Successful Referrals: ${analytics.successfulReferrals}
Trust Score: ${analytics.trustScore}
Completion Rate: ${analytics.completionRate}%
Local Value Created: $${analytics.localValueCreated}
    `.trim();

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uniledge-analytics-${timeRange}-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Calculate trend values
  const getTrendValue = (metric) => {
    if (!analytics) return '+0';
    
    switch (metric) {
      case 'saved':
        return analytics.periodSaved > 0 ? `+$${analytics.periodSaved}` : '$0';
      case 'tokens':
        return analytics.tokensEarned > 0 ? `+${analytics.tokensEarned}` : '0';
      case 'jobs':
        return analytics.jobsCompleted > 0 ? `+${analytics.jobsCompleted}` : '0';
      case 'trust':
        return analytics.trustScore >= 3.5 ? '+0.2' : '—';
      default:
        return '+0';
    }
  };

  // Get savings trend title
  const getSavingsTrendTitle = () => {
    switch (timeRange) {
      case 'week':
        return 'Daily Savings This Week';
      case 'month':
        return 'Weekly Savings This Month';
      case 'year':
        return 'Monthly Savings (6 Month View)';
      default:
        return 'Savings Trend';
    }
  };

  // Loading state
  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="bg-white shadow">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center">
              <button onClick={() => navigate(-1)} className="mr-4 p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-2xl font-bold">Your Analytics</h1>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading your analytics...</p>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="bg-white shadow">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center">
              <button onClick={() => navigate(-1)} className="mr-4 p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-2xl font-bold">Your Analytics</h1>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 mt-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-red-800 mb-2">Unable to Load Analytics</h2>
            <p className="text-red-600 mb-4">{error.message}</p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  // Default values if no data yet
  const safeAnalytics = analytics || {
    periodSaved: 0,
    monthSaved: 0,
    yearSaved: 0,
    lifetimeSaved: 0,
    tokensEarned: 0,
    lifetimeTokensEarned: 0,
    lifetimeTokensRedeemed: 0,
    tokenBalance: 0,
    jobsCompleted: 0,
    lifetimeJobsCompleted: 0,
    completionRate: 100,
    bestMonth: 0,
    totalReferrals: 0,
    successfulReferrals: 0,
    referralEarnings: 0,
    trustScore: 3.0,
    savingsByCategory: [],
    earningsOverTime: [],
    jobsByType: [],
    savingsTrend: [],
    avgSavingsPerPurchase: 0,
    avgTokensPerJob: 0,
    localValueCreated: 0
  };

  // Get earned achievements (max 4 for display)
  const earnedAchievements = achievements?.achieved?.slice(0, 4) || [];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Your Analytics</h1>
                <p className="text-sm text-gray-600">Track your savings and earnings</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Token Balance - Link to Wallet */}
              <button
                onClick={() => navigate('/wallet')}
                className="flex items-center gap-2 px-3 py-2 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 border border-yellow-200"
                title="View Wallet"
              >
                <Coins size={18} />
                <span className="font-medium">{safeAnalytics.tokenBalance?.toLocaleString() || 0}</span>
                <span className="text-xs text-yellow-600">(${((safeAnalytics.tokenBalance || 0) / 10).toFixed(2)})</span>
              </button>
              <button
                onClick={() => refetch()}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Refresh"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={handleExport}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Download size={16} className="mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="container mx-auto px-4 mt-6">
        <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg w-fit">
          {['week', 'month', 'year'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-md capitalize transition-all ${
                timeRange === range
                  ? 'bg-white shadow text-blue-600 font-medium'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 mt-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title={`Saved This ${timeRange.charAt(0).toUpperCase() + timeRange.slice(1)}`}
            value={`$${safeAnalytics.periodSaved}`}
            icon={<DollarSign size={20} />}
            trend="up"
            trendValue={getTrendValue('saved')}
            color="green"
          />
          <StatCard
            title="Tokens Earned"
            value={safeAnalytics.tokensEarned.toLocaleString()}
            icon={<Award size={20} />}
            trend="up"
            trendValue={getTrendValue('tokens')}
            color="yellow"
          />
          <StatCard
            title="Jobs Completed"
            value={safeAnalytics.jobsCompleted}
            icon={<TrendingUp size={20} />}
            trend="up"
            trendValue={getTrendValue('jobs')}
            color="purple"
          />
          <StatCard
            title="Trust Score"
            value={safeAnalytics.trustScore.toFixed(1)}
            icon={<Users size={20} />}
            trend="up"
            trendValue={getTrendValue('trust')}
            color="blue"
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="overflow-hidden">
            <BarChart
              data={safeAnalytics.earningsOverTime}
              height={200}
              title={`Token Earnings This ${timeRange.charAt(0).toUpperCase() + timeRange.slice(1)}`}
            />
          </div>
          <div>
            <PieChart
              data={safeAnalytics.savingsByCategory}
              title="Savings by Category"
            />
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <LineChart
              data={safeAnalytics.savingsTrend}
              height={200}
              title={getSavingsTrendTitle()}
            />
          </div>
          <div className="overflow-hidden">
            <BarChart
              data={safeAnalytics.jobsByType}
              height={200}
              title="Jobs Completed by Type"
            />
          </div>
        </div>

        {/* Detailed Stats */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4">Detailed Statistics</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Savings Breakdown */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Savings Breakdown</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">This {timeRange.charAt(0).toUpperCase() + timeRange.slice(1)}</span>
                  <span className="font-medium">${safeAnalytics.periodSaved}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">This Month</span>
                  <span className="font-medium">${safeAnalytics.monthSaved}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">This Year</span>
                  <span className="font-medium">${safeAnalytics.yearSaved}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Lifetime</span>
                  <span className="font-medium">${safeAnalytics.lifetimeSaved}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="text-gray-600">Average per Purchase</span>
                  <span className="font-medium">${safeAnalytics.avgSavingsPerPurchase}</span>
                </div>
              </div>
            </div>

            {/* Earnings Performance */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Coins size={16} className="text-yellow-500" />
                Token Economy
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Lifetime Earned</span>
                  <span className="font-medium text-green-600">+{safeAnalytics.lifetimeTokensEarned?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Lifetime Redeemed</span>
                  <span className="font-medium text-orange-600">-{safeAnalytics.lifetimeTokensRedeemed?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="text-gray-600">Current Balance</span>
                  <span className="font-bold">{safeAnalytics.tokenBalance?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Value</span>
                  <span className="font-medium text-green-600">${((safeAnalytics.tokenBalance || 0) / 10).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="text-gray-600">Avg per Job</span>
                  <span className="font-medium">{safeAnalytics.avgTokensPerJob}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Best Month</span>
                  <span className="font-medium">{safeAnalytics.bestMonth} tokens</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Completion Rate</span>
                  <span className="font-medium text-green-600">{safeAnalytics.completionRate}%</span>
                </div>
                <button
                  onClick={() => navigate('/wallet')}
                  className="w-full mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center gap-1"
                >
                  View Full Token History →
                </button>
              </div>
            </div>

            {/* Referrals & Impact */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Referrals & Impact</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Referrals</span>
                  <span className="font-medium">{safeAnalytics.totalReferrals}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Successful Referrals</span>
                  <span className="font-medium text-green-600">{safeAnalytics.successfulReferrals}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Referral Earnings</span>
                  <span className="font-medium">{safeAnalytics.referralEarnings} tokens</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="text-gray-600">Lifetime Jobs</span>
                  <span className="font-medium">{safeAnalytics.lifetimeJobsCompleted}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="text-gray-600">Local Value Created</span>
                  <span className="font-medium text-green-600">
                    ${safeAnalytics.localValueCreated.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Your Achievements - ONLY SHOWS EARNED ACHIEVEMENTS (FIXED) */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Your Achievements</h3>
            <button
              onClick={() => navigate('/earn?tab=milestones')}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
            >
              View All
              <ChevronRight size={16} />
            </button>
          </div>
          
          {achievementsLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : earnedAchievements.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {earnedAchievements.map((achievement) => (
                  <div key={achievement.id} className="bg-white rounded-lg p-4 text-center shadow-sm border border-green-200">
                    <div className="text-3xl mb-2">{achievement.icon}</div>
                    <p className="text-sm font-medium text-gray-900">{achievement.name}</p>
                    <p className="text-xs text-gray-600">{achievement.description}</p>
                    <p className="text-xs text-green-600 mt-1 flex items-center justify-center gap-1">
                      <Coins size={12} />
                      +{achievement.tokens} earned
                    </p>
                  </div>
                ))}
              </div>
              
              {/* Progress indicator */}
              {achievements?.totalAvailable > 0 && (
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600">
                    {achievements.totalAchieved} of {achievements.totalAvailable} achievements unlocked ({achievements.percentComplete}%)
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 bg-white rounded-lg">
              <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No achievements yet</p>
              <p className="text-sm text-gray-500 mt-1">
                Complete activities to earn your first achievement!
              </p>
              <button
                onClick={() => navigate('/earn?tab=milestones')}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                View Available Achievements
              </button>
            </div>
          )}
        </div>

        {/* Data freshness indicator */}
        {analytics?.generatedAt && (
          <div className="text-center text-xs text-gray-400">
            Data generated: {new Date(analytics.generatedAt).toLocaleString()}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default AnalyticsDashboardPage;